
import { Dialect, Language, ServiceDefinition, Plan, IntegrationApp } from './types';

export const DIALECTS = [
  { id: 'najdi', name: { ar: 'نجدي', en: 'Najdi' }, icon: '🏜️', color: 'emerald' },
  { id: 'hijazi', name: { ar: 'حجازي', en: 'Hijazi' }, icon: '🌊', color: 'blue' },
  { id: 'southern', name: { ar: 'جنوبي', en: 'Southern' }, icon: '⛰️', color: 'orange' },
  { id: 'eastern', name: { ar: 'شرقي', en: 'Eastern' }, icon: '🛢️', color: 'slate' }
] as const;

export const VOICES = [
  { id: 'Kore', name: 'Kore', gender: 'male', label: { ar: 'كوري (رسمي)', en: 'Kore (Formal)' } },
  { id: 'Puck', name: 'Puck', gender: 'male', label: { ar: 'باك (ودود)', en: 'Puck (Friendly)' } },
  { id: 'Charon', name: 'Charon', gender: 'male', label: { ar: 'تشارون (عميق)', en: 'Charon (Deep)' } },
  { id: 'Fenrir', name: 'Fenrir', gender: 'male', label: { ar: 'فينرير (قوي)', en: 'Fenrir (Strong)' } },
  { id: 'Zephyr', name: 'Zephyr', gender: 'male', label: { ar: 'زفير (ناعم)', en: 'Zephyr (Soft)' } },
  { id: 'Aoide', name: 'Aoide', gender: 'female', label: { ar: 'أويدي (هادئ)', en: 'Aoide (Calm)' } }
] as const;

export const SERVICES_DATA: ServiceDefinition[] = [
  // Media
  {
    id: 'dubbing',
    category: 'media',
    title: 'دوبلاج اللهجات',
    description: 'معالجة الفيديو ومزامنة الشفاه باللهجات السعودية بدقة عالية.',
    icon: 'fa-microphone-lines',
    color: 'from-rose-500 to-pink-600',
    isPro: true,
    isNew: true,
    integrations: ['youtube', 'premiere', 'drive'],
    capabilities: ['Lip Sync', 'Voice Cloning', 'Multi-speaker']
  },
  {
    id: 'news',
    category: 'media',
    title: 'الموجز الإخباري',
    description: 'توليد نشرات إخبارية احترافية بلهجة رسمية أو بيضاء.',
    icon: 'fa-newspaper',
    color: 'from-blue-500 to-indigo-600',
    isPro: true,
    integrations: ['wordpress', 'twitter', 'telegram'],
    capabilities: ['Formal Dialect', 'Daily Summaries', 'RSS Feed']
  },
  // Creative
  {
    id: 'story',
    category: 'creative',
    title: 'حكواتي لهجة',
    description: 'سرد قصصي تفاعلي مستوحى من التراث والتاريخ السعودي.',
    icon: 'fa-book-open',
    color: 'from-amber-500 to-orange-600',
    isPro: false,
    integrations: ['kindle', 'pdf'],
    capabilities: ['Heritage Context', 'Character Gen', 'Plot Twist']
  },
  {
    id: 'poetry',
    category: 'creative',
    title: 'الشاعر النبطي',
    description: 'نظم قصائد نبطية موزونة حسب الطلب والمناسبة.',
    icon: 'fa-feather-pointed',
    color: 'from-emerald-500 to-teal-600',
    isPro: false,
    isNew: true,
    integrations: ['instagram', 'tiktok'],
    capabilities: ['Nabati', 'Rhyme Check', 'Occasions']
  },
  // Business
  {
    id: 'email',
    category: 'business',
    title: 'مراسل الشركات',
    description: 'صياغة خطابات رسمية وإيميلات بأسلوب إداري محترف.',
    icon: 'fa-envelope-open-text',
    color: 'from-slate-600 to-slate-800',
    isPro: true,
    integrations: ['gmail', 'outlook', 'slack'],
    capabilities: ['Formal Tone', 'Reply Draft', 'Summarization']
  },
  {
    id: 'legal',
    category: 'business',
    title: 'المستشار القانوني',
    description: 'صياغة العقود ومراجعة البنود وفق الأنظمة المحلية.',
    icon: 'fa-scale-balanced',
    color: 'from-slate-700 to-black',
    isPro: true,
    integrations: ['word', 'pdf'],
    capabilities: ['Contracts', 'Regulations', 'Review']
  },
  // Content
  {
    id: 'script',
    category: 'content',
    title: 'كاتب السيناريو',
    description: 'كتابة نصوص إعلانية وسينمائية باللهجة المحلية.',
    icon: 'fa-video',
    color: 'from-violet-500 to-purple-600',
    isPro: true,
    integrations: ['youtube', 'notion'],
    capabilities: ['Scene Breakdown', 'Dialogue', 'Storyboarding']
  },
  {
    id: 'social',
    category: 'content',
    title: 'مدير التواصل',
    description: 'إنشاء ردود ومنشورات للتواصل الاجتماعي بروح سعودية.',
    icon: 'fa-hashtag',
    color: 'from-pink-500 to-rose-400',
    isPro: false,
    integrations: ['twitter', 'instagram', 'linkedin'],
    capabilities: ['Viral Hooks', 'Engagement', 'Scheduling']
  },
  // Dev / Data
  {
    id: 'analytics',
    category: 'dev',
    title: 'تحليل المشاعر',
    description: 'فهم انطباعات العملاء من النصوص والمكالمات المحلية.',
    icon: 'fa-chart-pie',
    color: 'from-cyan-500 to-blue-600',
    isPro: true,
    isNew: true,
    integrations: ['salesforce', 'zendesk', 'sheets'],
    capabilities: ['Sentiment', 'Intent Detect', 'Report Gen']
  },
  {
    id: 'translation',
    category: 'dev',
    title: 'المترجم المحلي',
    description: 'تعريب النصوص والمصطلحات التقنية إلى سياق ثقافي.',
    icon: 'fa-language',
    color: 'from-lime-500 to-green-600',
    isPro: false,
    integrations: ['word', 'chrome'],
    capabilities: ['Localization', 'Cultural Adapt', 'Idioms']
  },
];

export const INTEGRATION_APPS_DATA: IntegrationApp[] = [
  {
    id: 'google-maps',
    name: 'Google Maps',
    description: 'المساعد الملاحي السعودي',
    icon: 'fa-map-location-dot',
    color: 'from-green-500 to-emerald-600',
    connected: false,
    config: { voice: 'Kore', dialect: 'najdi', style: 'driver', avoidTolls: false, speedAlert: true },
    permissions: ['Location', 'Audio Overlay', 'Microphone'],
    stats: { requests: 0, timeSaved: 0 }
  },
  {
    id: 'phone-translator',
    name: 'Call Translator',
    description: 'ترجمة فورية للمكالمات',
    icon: 'fa-phone-volume',
    color: 'from-blue-500 to-cyan-500',
    connected: false,
    config: { dialect: 'najdi', voice: 'Kore', autoTranslate: true, muteOriginal: false, autoAnswer: false },
    permissions: ['Phone State', 'Microphone', 'Call Processing'],
    stats: { requests: 0, timeSaved: 0 }
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    description: 'مدير الردود الذكي',
    icon: 'fa-whatsapp',
    color: 'from-emerald-400 to-green-500',
    connected: false,
    config: { autoReply: true, tone: 'casual', summarizeVoice: true },
    permissions: ['Notifications', 'Accessibility'],
    stats: { requests: 0, timeSaved: 0 }
  },
  {
    id: 'spotify',
    name: 'Spotify',
    description: 'دي جي (DJ) تفاعلي',
    icon: 'fa-spotify',
    color: 'from-green-400 to-green-600',
    connected: false,
    config: { djPersonality: 'energetic', mixLocal: true },
    permissions: ['Now Playing'],
    stats: { requests: 0, timeSaved: 0 }
  }
];

export const PLANS_DATA: Plan[] = [
  {
    id: 'free',
    name: 'باقة البداية',
    price: 'مجاناً',
    period: 'للأبد',
    features: ['١٥ رسالة يومياً', 'لهجات أساسية', 'دعم محدود'],
    buttonText: 'خطتك الحالية',
    highlight: false,
    icon: 'fa-seedling',
    color: 'slate',
    limits: {
      messagesPerDay: 15,
      agentsCount: 1,
      flowsCount: 0,
      servicesPerDay: 5,
      allowFlows: false,
      allowProServices: false
    }
  },
  {
    id: 'pro',
    name: 'باقة المحترفين',
    price: '49',
    period: 'شهرياً',
    features: ['رسائل بلا حدود', 'جميع اللهجات', 'صوت عالي الدقة', 'استجابة سريعة', 'بدون إعلانات'],
    buttonText: 'اشترك الآن',
    highlight: true,
    icon: 'fa-rocket',
    url: 'https://buy.stripe.com/test_bJefZgep0deZfChaDvcZa00',
    color: 'emerald',
    limits: {
      messagesPerDay: -1, // Unlimited
      agentsCount: 10,
      flowsCount: 20,
      servicesPerDay: 500,
      allowFlows: true,
      allowProServices: true
    }
  },
  {
    id: 'enterprise',
    name: 'باقة الأعمال',
    price: 'تخصيص',
    period: 'حسب الحاجة',
    features: ['API مفتوح', 'تدريب خاص', 'دعم ٢٤/٧', 'لوحة تحكم'],
    buttonText: 'تواصل معنا',
    highlight: false,
    icon: 'fa-building-columns',
    color: 'blue',
    limits: {
      messagesPerDay: -1, // Unlimited
      agentsCount: 100,
      flowsCount: 100,
      servicesPerDay: -1,
      allowFlows: true,
      allowProServices: true
    }
  }
];

export const TRANSLATIONS: Record<Language, any> = {
  ar: {
    appName: "لهجة",
    tagline: "الذكاء الاصطناعي بروح سعودية",
    activeAI: "الذكاء النشط",
    chatRoom: "المحادثة",
    liveMode: "مباشر",
    flowsMode: "أتمتة",
    servicesMode: "الخدمات",
    robotMode: "وكيل",
    profileMode: "ملفي",
    integrationsMode: "التكامل",
    docsMode: "الوثائق",
    adminMode: "لوحة المدير",
    terminalMode: "موجه الأوامر",
    newChat: "محادثة جديدة",
    history: "المحادثات السابقة",
    clearAll: "مسح الكل",
    upgrade: "ترقية الحساب",
    settings: "إعدادات",
    logout: "خروج",
    placeholder: "تحدث معي باللهجة اللي تعجبك..",
    send: "إرسال",
    listening: "أسمعك بوضوح.. تفضل",
    analyzing: "جاري تحليل الحديث وتوليد الرد..",
    clickToStart: "انقر على الدائرة للبدء",
    noMessages: "أهلاً بك في منصة لهجة",
    welcomeText: "مساعدك الذكي الذي يفهمك بجميع اللهجات السعودية ويجيبك بروح أصلية.",
    startLive: "تنشيط المحادثة المباشرة",
    language: "اللغة",
    arabic: "العربية",
    english: "English",
    theme: "الوضع الليلي",
    speed: "سرعة النطق",
    dialect: "اللهجة الافتراضية",
    voice: "اختيار المتحدث",
    save: "تطبيق وحفظ التغييرات",
    profile: "الملف الشخصي",
    security: "الأمان والربط",
    appPrefs: "تفضيلات التطبيق",
    login: "دخول",
    role_user: "مستخدم",
    role_admin: "مدير",
    role_super: "سوبر",
    role_superadmin: "سوبر أدمن",
    register: "حساب جديد",
    email: "البريد الإلكتروني",
    password: "كلمة المرور",
    name: "الاسم",
    enterName: "ادخل اسمك",
    loginPlatform: "دخول للمنصة",
    sendVerify: "إرسال كود التحقق",
    verifyEmail: "تحقق من بريدك",
    verifyText: "أرسلنا كود التأكيد المكون من ٤ أرقام إلى بريدك:",
    confirmCode: "تأكيد الرمز",
    resendIn: "إعادة الإرسال خلال",
    resendNow: "إرسال الكود مجدداً",
    changeEmail: "تغيير البريد الإلكتروني",
    orWith: "أو بواسطة",
    loadingGoogle: "جاري تحميل خدمات جوجل...",
    rights: "جميع الحقوق محفوظة لشركة أسس الذكاء الرقمي",
    plansTitle: "الاشتراكات والترقيات",
    plansSubtitle: "باقات تناسب طموحك",
    plansDesc: "اختر الخطة التي تمنحك القدرة الكاملة على التواصل بذكاء وبأدق اللهجات السعودية",
    promoTitle: "لديك رمز دعوة؟",
    promoDesc: "استخدم الرمز للحصول على خصم خاص أو تفعيل باقة مجانية",
    activate: "تفعيل",
    promoSuccess: "تم تفعيل الرمز بنجاح! جاري الترقية...",
    promoError: "الرمز غير صالح أو انتهت صلاحيته",
    secure: "آمن بنسبة ١٠٪",
    support: "دعم فني متواصل",
    saudiMade: "صنع بكل فخر في المملكة 🇸🇦",
    planFree: "باقة البداية",
    planPro: "باقة المحترفين",
    planBusiness: "باقة الأعمال",
    free: "مجاناً",
    forever: "للأبد",
    monthly: "شهرياً",
    custom: "تخصيص",
    asNeeded: "حسب الحاجة",
    currentPlan: "خطتك الحالية",
    subscribe: "اشترك الآن",
    contactUs: "تواصل معنا",
    sar: "ريال",
    preferred: "الخيار المفضل",
    deleteChatTitle: "حذف المحادثة؟",
    deleteChatMsg: "هل أنت متأكد من حذف هذه المحادثة نهائياً؟",
    clearAllTitle: "مسح السجل بالكامل؟",
    clearAllMsg: "سيتم حذف جميع محادثاتك المخزنة. هل تريد الاستمرار؟",
    logoutTitle: "تسجيل الخروج؟",
    logoutMsg: "هل أنت متأكد من تسجيل الخروج؟ سنفتقدك كثيراً!",
    confirm: "تأكيد",
    cancel: "إلغاء",
    enableTTS: "تمكين الصوت للرسائل",
    deleteAccount: "مسح الحساب نهائياً",
    deleteAccountTitle: "حذف الحساب؟",
    deleteAccountMsg: "سيتم حذف كافة بياناتك ومحادثاتك نهائياً من هذا الجهاز. هل أنت متأكد؟",
    apiStatus: "مفتاح الربط (API Key)",
    apiKeyActive: "المفتاح نشط",
    apiKeyMissing: "غير متوفر",
    manageKeys: "إدارة المفاتيح",
    enterKeyPlaceholder: "ألصق المفتاح هنا أو استخدم الباركود...",
    scanBarcode: "مسح باركود",
    saveKey: "حفظ المفتاح",
    keySaved: "تم حفظ مفتاح الربط بنجاح",
    flowsTitle: "مركز التدفق (Flow Canvas)",
    flowsDesc: "بناء مسارات ذكية عبر ربط العقد الوظيفية للتحكم في البيانات.",
    addFlow: "إنشاء تدفق جديد",
    nodeLibrary: "مكتبة العقد",
    servicesLibrary: "الخدمات الذكية",
    canvas: "مساحة البناء",
    trigger: "مشغل (Trigger)",
    input: "إدخال (Input)",
    output: "إخراج (Output)",
    http: "طلب HTTP",
    processing: "معالجة بيانات",
    action: "إجراء (Action)",
    transform: "محول (Transform)",
    condition: "شرط (Condition)",
    ai_service: "خدمة ذكية",
    saveFlow: "حفظ التدفق",
    editNode: "تعديل العقدة",
    inputMap: "خريطة المدخلات",
    outputMap: "خريطة المخرجات",
    previewPayload: "معاينة البيانات",
    runTest: "تشغيل اختبار",
    noFlows: "لا توجد تدفقات مفعلة",
    servicesTitle: "مركز الخدمات المتكامل",
    servicesDesc: "منصة احترافية لربط الخدمات الذكية وتفعيل الأتمتة.",
    searchServices: "ابحث عن خدمة (دوبلاج، أخبار، تحليل...)",
    catAll: "الكل",
    catMedia: "الإعلام والإنتاج",
    catContent: "صناعة المحتوى",
    catBusiness: "الأعمال والشركات",
    catCreative: "الإبداع والفنون",
    catDev: "التطوير والبيانات",
    tryNow: "تشغيل الخدمة",
    automate: "بناء أتمتة (Flow)",
    locked: "ميزة مقفلة",
    proOnly: "للمحترفين فقط",
    new: "جديد",
    beta: "تجريبي",
    integrations: "التكاملات المتاحة",
    capabilities: "قدرات الخدمة",
    runLive: "تشغيل مباشر",
    buildFlow: "إنشاء مسار عمل",
    serviceDetails: "تفاصيل الخدمة",
    dragToadd: "اسحب للإضافة",
    nodes: "العقد",
    services: "الخدمات",
    robotTitle: "مركز قيادة الوكيل",
    robotDesc: "نظام تحكم متكامل للذكاء الاصطناعي التفاعلي.",
    robotActive: "متصل بالشبكة",
    robotIdle: "خامل",
    visionPrompt: "التوجيه الأساسي (System Prompt)",
    visionPlaceholder: "مثال: أنت موظف خدمة عملاء لشركة سياحة...",
    startRobot: "تشغيل النظام",
    stopRobot: "إيقاف",
    transcript: "محطة السجلات (Terminal)",
    robotStatus: "الحالة التشغيلية",
    robotThinking: "معالجة البيانات...",
    robotSpeaking: "جاري الإرسال...",
    robotListening: "استقبال صوتي...",
    autoReply: "الرد التلقائي",
    voiceOutput: "مخرج صوتي",
    sensitivity: "حساسية الاستماع",
    interruption: "سماح بالمقاطعة",
    knowledgeBase: "قاعدة المعرفة",
    uploadFile: "رفع ملف سياق",
    advancedSettings: "إعدادات متقدمة",
    agentName: "اسم الوكيل",
    agentRole: "الدور الوظيفي",
    dashboardTitle: "لوحة التحكم الشخصية",
    dashboardDesc: "نظرة شاملة على استهلاكك، خدماتك المفضلة، وتحليلات الأداء.",
    totalUsage: "إجمالي الطلبات",
    activeServices: "الخدمات النشطة",
    tokensConsumed: "التوكنز المستهلكة",
    savedTime: "الوقت الموفر",
    hours: "ساعة",
    usageByService: "توزيع الاستهلاك حسب الخدمة",
    weeklyActivity: "النشاط الأسبوعي",
    topServices: "الخدمات الأكثر استخداماً",
    requests: "طلب",
    manageBilling: "إدارة الاشتراك",
    integrationTitle: "بوابة الربط الخارجي",
    integrationDesc: "تحكم كامل في ربط الذكاء الاصطناعي مع تطبيقاتك اليومية",
    connect: "ربط",
    disconnect: "إلغاء الربط",
    configure: "ضبط الإعدادات",
    mapsTitle: "خرائط جوجل المتحدثة",
    mapsDesc: "استبدل صوت الملاحة الافتراضي بالمساعد السعودي الذكي لتوجيه دقيق وممتع.",
    simulateNav: "محاكاة الملاحة",
    stopSim: "إيقاف المحاكاة",
    restartTour: "إعادة جولة الاستكشاف",
    tourTitle: "مرحباً بك في عالم لهجة",
    tourNext: "التالي",
    tourSkip: "تخطي الجولة",
    tourFinish: "ابدأ الرحلة",
    tourStep1Title: "مركز القيادة (⌘K)",
    tourStep1Desc: "اضغط Ctrl+K للوصول السريع لأي خدمة أو أمر داخل الموقع.",
    tourStep2Title: "الوضع المباشر",
    tourStep2Desc: "تحدث مع الذكاء الاصطناعي صوتياً باللهجة التي تفضلها وبدون تأخير.",
    tourStep3Title: "الأتمتة والخدمات",
    tourStep3Desc: "اربط الخدمات ببعضها وابني مسارات عمل ذكية خاصة بك.",
    terminalTitle: "موجه الأوامر (System Console)",
    terminalPlaceholder: "ادخل أمراً (مثلاً: flow create test_flow)..."
  },
  en: {
    appName: "Lahja",
    tagline: "AI with a Saudi Soul",
    activeAI: "Active AI",
    chatRoom: "Chat",
    liveMode: "Live",
    flowsMode: "Flows",
    servicesMode: "Services",
    robotMode: "Agent",
    profileMode: "Profile",
    integrationsMode: "Integrations",
    docsMode: "Docs",
    adminMode: "Admin Dashboard",
    terminalMode: "Command Console",
    newChat: "New Chat",
    history: "Recent Chats",
    clearAll: "Clear All",
    upgrade: "Upgrade Account",
    settings: "Settings",
    logout: "Logout",
    placeholder: "Talk to me in any dialect...",
    send: "Send",
    listening: "Listening... go ahead",
    analyzing: "Analyzing and responding...",
    clickToStart: "Click the circle to start",
    noMessages: "Welcome to Lahja",
    welcomeText: "Your smart assistant that understands all Saudi dialects and responds with authentic spirit.",
    startLive: "Activate Live Conversation",
    language: "Language",
    arabic: "العربية",
    english: "English",
    theme: "Dark Mode",
    speed: "Speech Speed",
    dialect: "Default Dialect",
    voice: "Voice Selection",
    save: "Apply Changes",
    profile: "Profile",
    security: "Security & API",
    appPrefs: "App Prefs",
    login: "Login",
    role_user: "User",
    role_admin: "Admin",
    role_super: "Super",
    role_superadmin: "Super Admin",
    register: "Register",
    email: "Email Address",
    password: "Password",
    name: "Name",
    enterName: "Enter your name",
    loginPlatform: "Login to Platform",
    sendVerify: "Send Verification Code",
    verifyEmail: "Verify Email",
    verifyText: "We sent a 4-digit code to your email:",
    confirmCode: "Confirm Code",
    resendIn: "Resend in",
    resendNow: "Resend Code Now",
    changeEmail: "Change Email",
    orWith: "Or with",
    loadingGoogle: "Loading Google services...",
    rights: "All rights reserved to Ossos Digital Intelligence",
    plansTitle: "Subscriptions & Upgrades",
    plansSubtitle: "Plans for your Ambition",
    plansDesc: "Choose the plan that gives you full power to communicate smartly in precise Saudi dialects",
    promoTitle: "Have a promo code?",
    promoDesc: "Use the code to get a special discount or activate a free package",
    activate: "Activate",
    promoSuccess: "Promo code activated! Upgrading...",
    promoError: "Invalid or expired promo code",
    secure: "100% Secure",
    support: "Continuous Support",
    saudiMade: "Proudly made in Saudi 🇸🇦",
    planFree: "Starter Plan",
    planPro: "Pro Plan",
    planBusiness: "Business Plan",
    free: "Free",
    forever: "Forever",
    monthly: "Monthly",
    custom: "Custom",
    asNeeded: "As needed",
    currentPlan: "Current Plan",
    subscribe: "Subscribe Now",
    contactUs: "Contact Us",
    sar: "SAR",
    preferred: "Preferred Choice",
    deleteChatTitle: "Delete Chat?",
    deleteChatMsg: "Are you sure you want to delete this chat permanently?",
    clearAllTitle: "Clear All History?",
    clearAllMsg: "All your stored chats will be deleted. Do you want to continue?",
    logoutTitle: "Logout?",
    logoutMsg: "Are you sure you want to logout? We will miss you!",
    confirm: "Confirm",
    cancel: "Cancel",
    enableTTS: "Enable Voice for Messages",
    deleteAccount: "Permanently Delete Account",
    deleteAccountTitle: "Delete Account?",
    deleteAccountMsg: "All your data and chats will be permanently deleted from this device. Are you sure?",
    apiStatus: "API Key Configuration",
    apiKeyActive: "Active",
    apiKeyMissing: "Missing",
    manageKeys: "Manage Keys",
    enterKeyPlaceholder: "Paste Key or Scan Barcode...",
    scanBarcode: "Scan Barcode",
    saveKey: "Save Key",
    keySaved: "API Key saved successfully",
    flowsTitle: "Flow Canvas",
    flowsDesc: "Construct intelligent pipelines by linking functional nodes to control data flow.",
    addFlow: "New Flow",
    nodeLibrary: "Nodes",
    servicesLibrary: "Services",
    canvas: "Canvas",
    trigger: "Trigger",
    input: "Input",
    output: "Output",
    http: "HTTP Request",
    processing: "Data Processing",
    action: "Action",
    transform: "Transform",
    condition: "Condition",
    ai_service: "AI Service",
    saveFlow: "Save Flow",
    editNode: "Edit Node",
    inputMap: "Input Map",
    outputMap: "Output Map",
    previewPayload: "Preview Data",
    runTest: "Run Test",
    noFlows: "No active flows",
    servicesTitle: "Enterprise Services Hub",
    servicesDesc: "A professional platform for connecting intelligent services and activating automation.",
    searchServices: "Search for services (Dubbing, News, Analytics...)",
    catAll: "All",
    catMedia: "Media & Production",
    catContent: "Content Creation",
    catBusiness: "Business",
    catCreative: "Arts & Creativity",
    catDev: "Development & Data",
    tryNow: "Run Service",
    automate: "Build Automation",
    locked: "Locked",
    proOnly: "Pro Only",
    new: "New",
    beta: "Beta",
    integrations: "Available Integrations",
    capabilities: "Service Capabilities",
    runLive: "Run Live",
    buildFlow: "Create Workflow",
    serviceDetails: "Service Details",
    dragToadd: "Drag/Click to add",
    nodes: "Nodes",
    services: "Services",
    robotTitle: "Agent Command Center",
    robotDesc: "Integrated control system for interactive AI operations.",
    robotActive: "System Online",
    robotIdle: "Idle",
    visionPrompt: "System Prompt (Vision)",
    visionPlaceholder: "Ex: You are a sales agent for a tourism company...",
    startRobot: "Initialize System",
    stopRobot: "Terminate",
    transcript: "Terminal Logs",
    robotStatus: "Operational Status",
    robotThinking: "Processing...",
    robotSpeaking: "Transmitting...",
    robotListening: "Receiving Audio...",
    autoReply: "Auto Reply",
    voiceOutput: "Voice Output",
    sensitivity: "Listen Sensitivity",
    interruption: "Allow Interruption",
    knowledgeBase: "Knowledge Base",
    uploadFile: "Upload Context File",
    advancedSettings: "Advanced Settings",
    agentName: "Agent Name",
    agentRole: "Role Description",
    dashboardTitle: "User Dashboard",
    dashboardDesc: "Comprehensive overview of your usage, favorite services, and performance analytics.",
    totalUsage: "Total Requests",
    activeServices: "Active Services",
    tokensConsumed: "Tokens Consumed",
    savedTime: "Time Saved",
    hours: "hrs",
    usageByService: "Usage Distribution",
    weeklyActivity: "Weekly Activity",
    topServices: "Top Services",
    requests: "req",
    manageBilling: "Manage Billing",
    integrationTitle: "External Integrations",
    integrationDesc: "Full control over connecting AI with your daily apps",
    connect: "Connect",
    disconnect: "Disconnect",
    configure: "Configure",
    mapsTitle: "Talking Google Maps",
    mapsDesc: "Replace standard navigation voice with the Saudi Smart Assistant for accurate and fun guidance.",
    simulateNav: "Simulate Navigation",
    stopSim: "Stop Simulation",
    restartTour: "Restart Discovery Tour",
    tourTitle: "Welcome to Lahja",
    tourNext: "Next",
    tourSkip: "Skip Tour",
    tourFinish: "Start Journey",
    tourStep1Title: "Command Center (⌘K)",
    tourStep1Desc: "Press Ctrl+K to quickly access any service or command.",
    tourStep2Title: "Live Mode",
    tourStep2Desc: "Talk to AI via voice in your preferred dialect with zero lag.",
    tourStep3Title: "Automation & Services",
    tourStep3Desc: "Connect services and build your own custom AI workflows.",
    terminalTitle: "System Console (CLI)",
    terminalPlaceholder: "Enter command (e.g., plan upgrade pro)..."
  }
};

export const getSystemInstruction = (dialect: Dialect, lang: Language) => {
  const instructions: Record<Dialect, string> = {
    najdi: `You are a smart assistant speaking the Najdi Saudi dialect fluently. Use terms like: "وش السالفة", "يا هلا والله", "أبشر". Keep it concise and friendly.`,
    hijazi: `You are a smart assistant speaking the Hijazi Saudi dialect (Jeddah, Makkah, Madinah). Use terms like: "يا واد", "إيش الهرجة", "يا لطيف". Be polite and warm.`,
    southern: `You are a smart assistant speaking the Southern Saudi dialect. Use terms like: "يا مرحبا تراحيب المطر", "هيا تعال", "ايش حالك". Reflect southern hospitality.`,
    eastern: `You are a smart assistant speaking the Eastern Saudi dialect. Use terms like: "شخبارك", "وش الدنيا بك", "يا هلا ومسهلا". Be calm and friendly.`
  };

  const base = lang === 'ar' 
    ? `.يجب ان تكون ردودك مختصر  لا  تزيد عن سطر  واحد أنت مساعد ذكي "لهجة" من تطوير شركة أسس الذكاء الرقمي. تخصصك اللهجات السعودية.` 
    : `You are "Lahja", a smart assistant by Ossos Digital Intelligence, specializing in Saudi dialects.`;

  return `${base} ${instructions[dialect]} Respond in the chosen dialect even if the UI is in English, but you can explain things in English if the user asks in English.`;
};
